import React, { useEffect, useState } from 'react';
import { Provider, useDispatch } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import Keycloak from 'keycloak-js';
import * as Sentry from '@sentry/react';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import { SnackbarProvider } from 'notistack';
import { GrowthBookProvider, GrowthBook } from '@growthbook/growthbook-react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { SSOProvider } from '@vbee-holding/sso-pkg';
import { Box, CircularProgress } from '@mui/material';

import apis from '@src/apis';
import store from './redux/store';
import AppRouter from './router';
import theme from './styles/theme';
import {
  IAM_URL,
  IAM_REALM,
  IAM_CLIENT_ID,
  GB_API_HOST,
  GB_CLIENT_KEY,
  RECAPTCHA_V3_SITE_KEY,
  CLIENT_ID,
  SSO_URL,
  SSO_API_URL,
  MY_ACCOUNT_URL,
  ENV,
  DSN_SENTRY_URL,
} from './configs';
import { getCookie } from './utils/cookie';
import NotiHandler from './errors/NotiHandler';
import FontLoader from './containers/FontLoader';
import useFeatureFlags from './hooks/useFeatureFlags';
import { FEATURE_KEYS } from './configs/featureKeys';
import { getDomain } from './utils/url';
import { updateLocation } from './redux/location/actions';
import { COUNTRY } from './constants/location';
import useLocation from './hooks/useLocation';
import useKCTokenHandling from './hooks/useKCTokenHandling';

const growthbook = new GrowthBook({
  apiHost: GB_API_HOST,
  clientKey: GB_CLIENT_KEY,
  enableDevMode: true,
  subscribeToChanges: true,
});

const LocationProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { isFeatureFlagsReady } = useFeatureFlags();

  const [isLoading, setIsLoading] = useState(true);

  const fetchCountry = async () => {
    const res = await apis.country.getCountry();
    dispatch(
      updateLocation({
        ip: res?.ip,
        country: res?.country || COUNTRY.VN,
      }),
    );
    setIsLoading(false);
  };

  useEffect(() => {
    fetchCountry();
  }, []);

  // Wait for feature flags to be ready and location to be fetched before rendering the children components
  if (isLoading || !isFeatureFlagsReady) {
    return (
      <Box
        height="100vh"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return children;
};

const keycloak = Keycloak({
  url: `${IAM_URL}/auth`,
  realm: IAM_REALM,
  clientId: IAM_CLIENT_ID,
});

const notistackRef = React.createRef();

const SSOProviderWrapper = () => {
  const { getFeatureValue } = useFeatureFlags();
  const { ip } = useLocation();
  const useSSO = getFeatureValue(FEATURE_KEYS.NEW_SSO_STUDIO, { ip });
  const useSentry = getFeatureValue(FEATURE_KEYS.SENTRY_AIVOICE_WEB_APP);
  const configSSO = {
    clientId: CLIENT_ID,
    ssoUrl: SSO_URL,
    ssoApiUrl: SSO_API_URL,
    myAccountUrl: MY_ACCOUNT_URL,
    clientDomain: getDomain(),
    options: {
      autoLogin: true,
      useSSO,
    },
  };

  if (useSentry) {
    Sentry.init({
      environment: ENV,
      dsn: DSN_SENTRY_URL,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      tracesSampleRate: useSentry?.tracesSampleRate || 1.0,
      replaysSessionSampleRate: useSentry?.replaysSessionSampleRate || 0.1,
      replaysOnErrorSampleRate: useSentry?.replaysOnErrorSampleRate || 1.0,
    });
  }

  return (
    <SSOProvider config={configSSO}>
      <AppRouter />
    </SSOProvider>
  );
};

const KeycloakProviderWrapper = () => {
  const refreshToken = getCookie('refreshToken');
  const { handleReceivingTokens, handleEvent } = useKCTokenHandling();

  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={{
        onLoad: 'check-sso',
        refreshToken,
      }}
      onTokens={handleReceivingTokens}
      onEvent={handleEvent}
    >
      <SSOProviderWrapper />
    </ReactKeycloakProvider>
  );
};

const App = () => {
  useEffect(() => {
    // Load features asynchronously when the app renders
    growthbook.loadFeatures();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <GrowthBookProvider growthbook={growthbook}>
        <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_V3_SITE_KEY}>
          <Provider store={store()}>
            <LocationProvider>
              <SnackbarProvider
                preventDuplicate
                maxSnack={3}
                ref={notistackRef}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <NotiHandler>
                  <KeycloakProviderWrapper />
                </NotiHandler>
              </SnackbarProvider>
            </LocationProvider>
          </Provider>
        </GoogleReCaptchaProvider>
        <FontLoader />
      </GrowthBookProvider>
    </ThemeProvider>
  );
};

export default App;
